import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html, nothing } from "lit";
import { property, query, state } from "lit/decorators.js";
import "@progressive-development/pd-icon/pd-icon";
//#region src/pd-tab/PdTab.ts
/**
* Tab navigation component with header tabs and content slots.
*
* Supports horizontal (default) and vertical orientation, plus
* responsive label switching (full -> short labels) when tabs
* overflow their container.
*
* @tagname pd-tab
* @summary Tab navigation with horizontal/vertical orientation and responsive labels.
*
* @event tab-selected - Fired when a tab is selected. Emits the tab key.
*
* @slot [key] - Named slots for each tab content, matching the tab key.
*
* @cssprop --pd-tab-header-padding - Tab header padding. Default: `0.5em 1em`.
* @cssprop --pd-tab-font-size - Tab font size. Default: `0.875rem`.
* @cssprop --pd-tab-font-size-compact - Font size in compact mode. Default: `0.8rem`.
* @cssprop --pd-tab-text-col - Tab text color.
* @cssprop --pd-tab-active-text-col - Active tab text color.
* @cssprop --pd-tab-active-indicator-col - Active tab indicator color.
* @cssprop --pd-tab-active-indicator-height - Active tab indicator thickness. Default: `2px`.
* @cssprop --pd-tab-hover-bg-col - Tab hover background.
* @cssprop --pd-tab-disabled-text-col - Disabled tab text color.
* @cssprop --pd-tab-icon-size - Icon size in tab headers. Default: `1.125em`.
* @cssprop --pd-tab-icon-gap - Gap between icon and label. Default: `0.35em`.
* @cssprop --pd-tab-content-padding - Content area padding.
* @cssprop --pd-tab-content-border - Content area border.
*/
var PdTab = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.tabs = [];
		this.orientation = "horizontal";
		this._resizeObserver = null;
	}
	static {
		this.styles = [css`
      :host {
        display: block;
        --_font-size: var(--pd-tab-font-size, 0.875rem);
        --_font-size-compact: var(--pd-tab-font-size-compact, 0.8rem);
        --_active-col: var(
          --pd-tab-active-indicator-col,
          var(--pd-default-col)
        );
        --_active-height: var(--pd-tab-active-indicator-height, 2px);
        --_icon-size: var(--pd-tab-icon-size, 1.125em);
      }

      /* --- Wrapper --- */
      .tab-wrapper {
        display: flex;
        flex-direction: column;
      }

      :host([orientation="vertical"]) .tab-wrapper {
        flex-direction: row;
      }

      /* --- Header container --- */
      .header-container {
        display: flex;
        flex-wrap: nowrap;
        border-bottom: 1px solid var(--pd-default-disabled-light-col, #e5e7eb);
        font-size: var(--_font-size);
      }

      :host([orientation="vertical"]) .header-container {
        flex-direction: column;
        border-bottom: none;
        border-right: 1px solid var(--pd-default-disabled-light-col, #e5e7eb);
      }

      /* --- Tab header --- */
      .tab-header {
        padding: var(--pd-tab-header-padding, 0.5em 1em);
        font-family: var(--pd-default-font-title-family);
        color: var(--pd-tab-text-col, var(--pd-default-font-title-col));
        cursor: pointer;
        background: transparent;
        border: none;
        border-bottom: var(--_active-height) solid transparent;
        margin-bottom: calc(-1 * var(--_active-height) + 1px);
        white-space: nowrap;
        transition:
          color 0.2s,
          border-color 0.2s,
          background-color 0.2s;
        display: inline-flex;
        align-items: center;
        gap: var(--pd-tab-icon-gap, 0.35em);
        flex-shrink: 0;
      }

      :host([orientation="vertical"]) .tab-header {
        border-bottom: none;
        margin-bottom: 0;
        border-left: var(--_active-height) solid transparent;
        margin-left: calc(-1 * var(--_active-height) + 1px);
      }

      .tab-header:hover {
        background-color: var(--pd-tab-hover-bg-col, rgba(0, 0, 0, 0.04));
      }

      .tab-header.active-header {
        color: var(--pd-tab-active-text-col, var(--_active-col));
        border-bottom-color: var(--_active-col);
        font-weight: 500;
      }

      :host([orientation="vertical"]) .tab-header.active-header {
        border-bottom-color: transparent;
        border-left-color: var(--_active-col);
      }

      .tab-header.disabled-header {
        pointer-events: none;
        color: var(
          --pd-tab-disabled-text-col,
          var(--pd-default-disabled-text-col)
        );
        opacity: 0.5;
        cursor: not-allowed;
      }

      .tab-header:focus {
        outline: none;
      }

      .tab-header:focus-visible {
        outline: 2px solid var(--pd-focus-ring-col);
        outline-offset: -2px;
      }

      /* --- Compact mode (short labels) --- */
      :host([data-compact]) .header-container {
        font-size: var(--_font-size-compact);
      }

      .full-label {
        display: inline;
      }

      .short-label {
        display: none;
      }

      :host([data-compact]) .full-label {
        display: none;
      }

      :host([data-compact]) .short-label {
        display: inline;
      }

      /* --- Icon --- */
      .tab-icon {
        --pd-icon-size: var(--_icon-size);
        flex-shrink: 0;
      }

      /* --- Panels container --- */
      .panels-container {
        flex: 1;
        min-width: 0;
        min-height: 0;
      }

      /* --- Content panels --- */
      .slot-container {
        display: none;
      }

      .active-slot {
        padding: var(--pd-tab-content-padding, 1em);
        display: block;
        border: var(
          --pd-tab-content-border,
          1px solid var(--pd-default-disabled-light-col, #e5e7eb)
        );
        border-top: none;
      }

      :host([orientation="vertical"]) .active-slot {
        border-top: var(
          --pd-tab-content-border,
          1px solid var(--pd-default-disabled-light-col, #e5e7eb)
        );
        border-left: none;
      }
    `];
	}
	willUpdate(changedProperties) {
		if (changedProperties.has("defaultTab") && this.defaultTab) this._activeTab = this.defaultTab;
	}
	updated(changedProperties) {
		if (changedProperties.has("tabs")) this._checkOverflow();
	}
	firstUpdated() {
		this._resizeObserver = new ResizeObserver(() => this._checkOverflow());
		this._resizeObserver.observe(this._headerContainer);
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		if (this._resizeObserver) {
			this._resizeObserver.disconnect();
			this._resizeObserver = null;
		}
	}
	render() {
		const isVertical = this.orientation === "vertical";
		return html`
      <div class="tab-wrapper">
        <div
          id="hc"
          class="header-container"
          role="tablist"
          aria-orientation="${isVertical ? "vertical" : "horizontal"}"
          @keydown=${this._onKeyDown}
        >
          ${this.tabs.map((t) => html`
              <div
                class="tab-header ${this._activeTab === t.key ? "active-header" : ""} ${t.disabled ? "disabled-header" : ""}"
                data-key="${t.key}"
                @click="${this._tabSelected}"
                role="tab"
                id="${`tab-${t.key}`}"
                aria-selected="${this._activeTab === t.key}"
                aria-controls="${`panel-${t.key}`}"
                aria-disabled="${t.disabled || false}"
                tabindex="${this._activeTab === t.key && !t.disabled ? 0 : -1}"
              >
                ${t.pdIcon ? html`<pd-icon
                        class="tab-icon"
                        .icon="${t.pdIcon}"
                      ></pd-icon>` : nothing}
                <span class="full-label">${t.name}</span>
                <span class="short-label">${t.shortLabel ?? t.name}</span>
              </div>
            `)}
        </div>
        <div class="panels-container">
          ${this.tabs.map((t) => html`
              <div
                id="${`panel-${t.key}`}"
                role="tabpanel"
                aria-labelledby="${`tab-${t.key}`}"
                class="slot-container ${this._activeTab === t.key ? "active-slot" : ""}"
              >
                <slot name="${t.key}"></slot>
              </div>
            `)}
        </div>
      </div>
    `;
	}
	reset() {
		this._activeTab = this.defaultTab;
	}
	_checkOverflow() {
		this.removeAttribute("data-compact");
		const c = this._headerContainer;
		if ((this.orientation === "vertical" ? c.scrollHeight > c.clientHeight + 1 : c.scrollWidth > c.clientWidth + 1) && this.tabs.some((t) => t.shortLabel)) this.setAttribute("data-compact", "");
	}
	_onKeyDown(event) {
		const enabledTabs = this.tabs.filter((t) => !t.disabled);
		if (enabledTabs.length === 0) return;
		const currentIndex = enabledTabs.findIndex((t) => t.key === this._activeTab);
		let nextIndex = -1;
		const isVertical = this.orientation === "vertical";
		const nextKeys = isVertical ? ["ArrowDown"] : ["ArrowRight"];
		const prevKeys = isVertical ? ["ArrowUp"] : ["ArrowLeft"];
		if (nextKeys.includes(event.key)) {
			event.preventDefault();
			nextIndex = currentIndex < enabledTabs.length - 1 ? currentIndex + 1 : 0;
		} else if (prevKeys.includes(event.key)) {
			event.preventDefault();
			nextIndex = currentIndex > 0 ? currentIndex - 1 : enabledTabs.length - 1;
		} else if (event.key === "Home") {
			event.preventDefault();
			nextIndex = 0;
		} else if (event.key === "End") {
			event.preventDefault();
			nextIndex = enabledTabs.length - 1;
		} else return;
		if (nextIndex >= 0 && nextIndex < enabledTabs.length) {
			const nextTab = enabledTabs[nextIndex];
			this._activeTab = nextTab.key;
			this.dispatchEvent(new CustomEvent("tab-selected", {
				detail: this._activeTab,
				bubbles: true,
				composed: true
			}));
			this.updateComplete.then(() => {
				(this.shadowRoot?.querySelector(`[data-key="${nextTab.key}"]`))?.focus();
			});
		}
	}
	_tabSelected(e) {
		const key = e.target.closest("[data-key]")?.dataset?.key;
		if (!key) return;
		this._activeTab = key;
		this.dispatchEvent(new CustomEvent("tab-selected", {
			detail: this._activeTab,
			bubbles: true,
			composed: true
		}));
	}
};
__decorate([property({ type: Array })], PdTab.prototype, "tabs", void 0);
__decorate([property({ type: String })], PdTab.prototype, "defaultTab", void 0);
__decorate([property({
	type: String,
	reflect: true
})], PdTab.prototype, "orientation", void 0);
__decorate([state()], PdTab.prototype, "_activeTab", void 0);
__decorate([query("#hc")], PdTab.prototype, "_headerContainer", void 0);
//#endregion
export { PdTab };
